import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Clock, BookCheck, FileText, Highlighter, StickyNote, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (iso) => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const Stat = ({ icon: Icon, label, value, color, testId }) => (
  <div style={{ background: '#f7fafc', borderRadius: '10px', padding: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
    <Icon className="w-5 h-5" style={{ color, flexShrink: 0 }} />
    <div>
      <p style={{ fontSize: '1.35rem', fontWeight: 700, color: '#2D3748', lineHeight: 1 }} data-testid={testId}>{value}</p>
      <p style={{ fontSize: '0.75rem', color: '#718096', marginTop: '0.2rem' }}>{label}</p>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginTop: '1.5rem' }}>
    <h4 style={{ fontSize: '0.8rem', fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.6rem' }}>{title}</h4>
    {children}
  </div>
);

const BookProgressRow = ({ book, onOpen }) => (
  <button onClick={() => onOpen && onOpen(book.id)} data-testid={`history-book-${book.id}`}
    style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '0.5rem 0', cursor: 'pointer', borderBottom: '1px solid #edf2f7' }}>
    <div style={{ width: '36px', height: '50px', borderRadius: '4px', overflow: 'hidden', background: '#e2e8f0', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {book.capa_url ? <img src={book.capa_url.startsWith('http') ? book.capa_url : `${BACKEND_URL}${book.capa_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <BookOpen className="w-4 h-4" style={{ color: '#a0aec0' }} />}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={{ fontWeight: 600, color: '#2D3748', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{book.titulo}</p>
      <div style={{ height: '6px', background: '#edf2f7', borderRadius: '3px', marginTop: '0.35rem', overflow: 'hidden' }}>
        <div style={{ width: `${book.progress_pct}%`, height: '100%', background: book.finished ? '#38a169' : '#68AAB0', transition: 'width 0.4s ease' }} />
      </div>
      <p style={{ fontSize: '0.72rem', color: '#718096', marginTop: '0.25rem' }}>
        {book.pages_read} de {book.total_pages} páginas · <span data-testid={`history-book-pct-${book.id}`}>{book.progress_pct}%</span>{book.finished ? ' · Concluído' : ''}
      </p>
    </div>
  </button>
);

export const ReadingHistoryModal = ({ editorId, onClose, onOpenBook, url, headers, title = 'Meu Histórico', thirdPerson = false }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const token = localStorage.getItem('subscriberToken');
    axios.get(url || `${API}/digital/${editorId}/subscriber/history?auth=${token}`, headers ? { headers } : undefined)
      .then((r) => setData(r.data))
      .catch(() => setError('Não foi possível carregar o histórico.'));
  }, [editorId, url]); // eslint-disable-line

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="history-modal" style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '560px', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, background: 'white' }}>
          <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#2D3748' }} data-testid="history-title">{title}</h3>
          <button onClick={onClose} data-testid="history-close-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#718096' }}><X className="w-5 h-5" /></button>
        </div>
        <div style={{ padding: '1.25rem 1.5rem 1.5rem' }}>
          {error && <p style={{ color: '#c53030' }}>{error}</p>}
          {!data && !error && <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#68AAB0' }} /></div>}
          {data && (
            <>
              {data.simulated_note && <p data-testid="history-simulated-note" style={{ background: '#fffbeb', border: '1px solid #f6e05e', color: '#92400e', borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{data.simulated_note}</p>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                <Stat icon={BookCheck} label="Livros lidos (até o fim)" value={data.books_finished.length} color="#38a169" testId="history-books-finished" />
                <Stat icon={FileText} label="Páginas lidas" value={data.pages_read_total} color="#3182ce" testId="history-pages-read" />
                <Stat icon={Highlighter} label="Grifos" value={data.highlights_count} color="#d69e2e" testId="history-highlights" />
                <Stat icon={StickyNote} label="Notas" value={data.notes_count} color="#805ad5" testId="history-notes" />
              </div>

              <Section title="Livros lidos">
                {data.books_finished.length === 0
                  ? <p style={{ fontSize: '0.85rem', color: '#a0aec0' }}>Nenhum livro lido até a última página ainda.</p>
                  : <ul data-testid="history-finished-list" style={{ paddingLeft: '1.1rem', color: '#2D3748', fontSize: '0.9rem' }}>{data.books_finished.map((b) => <li key={b.id}>{b.titulo}</li>)}</ul>}
              </Section>

              <Section title={`Progresso por livro (${data.books_started})`}>
                {data.books.length === 0
                  ? <p style={{ fontSize: '0.85rem', color: '#a0aec0' }}>{thirdPerson ? 'O leitor ainda não abriu nenhum livro.' : 'Você ainda não abriu nenhum livro.'}</p>
                  : data.books.map((b) => <BookProgressRow key={b.id} book={b} onOpen={onOpenBook} />)}
              </Section>

              <Section title="Últimos 10 acessos">
                {data.last_accesses.length === 0
                  ? <p style={{ fontSize: '0.85rem', color: '#a0aec0' }}>Nenhum acesso registrado.</p>
                  : <div data-testid="history-access-list">{data.last_accesses.map((at, i) => (
                    <div key={at + i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0', fontSize: '0.85rem', color: '#4a5568' }}>
                      <Clock className="w-4 h-4" style={{ color: '#a0aec0' }} /> {fmtDate(at)}
                    </div>
                  ))}</div>}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
